import React, { useState, useEffect } from "react";
import apiService from "../../actions/apiService";

const Backdrop = ({ onClick }) => (
  <div
    className="fixed inset-0 bg-black/30 z-[110]"
    onClick={onClick}
    aria-hidden
  />
);

const ModalContainer = ({ children }) => (
  <div className="fixed mt-[10px] mb-[10px] inset-0 z-[110] flex items-center justify-center">
    <div className="w-full rounded-[12px] overflow-y-auto h-full max-w-[852px] bg-white overflow-hidden px-5 md:px-16 py-5 md:py-8">
      {children}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-[24px] font-rubik text-esdarkGray font-[500] mb-8">
    {children}
  </h3>
);

const Field = ({ label, children }) => (
  <div className="w-full">
    <label className="block text-caption opacity-50 text-esDarkBlue mb-2">
      {label}
    </label>
    {children}
  </div>
);

// Props: open, onClose, user, onUserUpdated
const UserInfoModal = ({ open, onClose, user, onUserUpdated }) => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    role: "",
    status: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Helper function to format relative time (e.g., "about 3 hours ago")
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "—";

    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return "just now";
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return diffInMinutes === 1
        ? "about 1 minute ago"
        : `about ${diffInMinutes} minutes ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return diffInHours === 1
        ? "about 1 hour ago"
        : `about ${diffInHours} hours ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return diffInDays === 1
        ? "about 1 day ago"
        : `about ${diffInDays} days ago`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return diffInWeeks === 1
        ? "about 1 week ago"
        : `about ${diffInWeeks} weeks ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return diffInMonths === 1
        ? "about 1 month ago"
        : `about ${diffInMonths} months ago`;
    }

    const diffInYears = Math.floor(diffInDays / 365);
    return diffInYears === 1
      ? "about 1 year ago"
      : `about ${diffInYears} years ago`;
  };

  // Helper function to get admin user display name
  const getAdminDisplayName = (user) => {
    if (!user) return "Admin user";
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    if (user.email) {
      return user.email.split("@")[0];
    }
    return "Admin user";
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setForm({
        firstName: "",
        lastName: "",
        role: "",
        status: "",
      });
      setUserData(null);
      setError(null);
      setUserLogs([]);
    }
  }, [open]);

  // Fetch user data when modal opens
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;

      setLoading(true);
      setError(null);
      try {
        const res = await apiService.get(`/admin/users/${user.id}`);
        if (res?.success) {
          const fetchedUser = res?.data?.user || res?.data;
          console.log("Fetched user data:", fetchedUser); // Debug log

          // Map is_blocked and is_active to status
          const status = fetchedUser.is_blocked
            ? "blocked"
            : fetchedUser.is_active !== false
            ? "active"
            : "inactive";

          // Set both userData and form state together
          setUserData(fetchedUser);
          setForm({
            firstName: fetchedUser.firstName || "N/A",
            lastName: fetchedUser.lastName || "N/A",
            role: fetchedUser.role || "",
            status: status,
          });
        } else {
          setError(res?.error || "Failed to load user data");
        }
      } catch (err) {
        console.error("Error fetching user:", err); // Debug log
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (open && user?.id) {
      fetchUserData();
    }
  }, [open, user?.id]);

  // Fetch user action logs when user data is loaded
  useEffect(() => {
    const fetchUserLogs = async () => {
      if (!user?.id || !userData) return;

      setLogsLoading(true);
      try {
        const res = await apiService.get(
          `/admin/users/${user.id}/action-logs`,
          {
            page: 1,
            limit: 10,
          }
        );
        if (res?.success) {
          setUserLogs(res.data?.notifications || []);
          console.log(
            "User logs fetched:",
            res.data?.notifications?.length || 0,
            "logs"
          );
        } else {
          console.error("Failed to fetch user logs:", res?.error);
          setUserLogs([]);
        }
      } catch (err) {
        console.error("Error fetching user logs:", err);
        setUserLogs([]);
      } finally {
        setLogsLoading(false);
      }
    };

    if (open && userData) {
      fetchUserLogs();
    }
  }, [open, user?.id, userData]);

  const handleSaveChanges = async () => {
    if (!user?.id || !userData) return;

    setLoading(true);
    setError(null);

    try {
      // Map status dropdown to is_active and is_blocked booleans
      let is_active = undefined;
      let is_blocked = undefined;

      if (form.status === "blocked") {
        is_blocked = true;
        is_active = userData.is_active; // Keep current is_active value
      } else if (form.status === "active") {
        is_active = true;
        is_blocked = false;
      } else if (form.status === "inactive") {
        is_active = false;
        is_blocked = false;
      }

      // Prepare update payload
      const updateData = {
        firstName: form.firstName !== "N/A" ? form.firstName : undefined,
        lastName: form.lastName !== "N/A" ? form.lastName : undefined,
        role: form.role || undefined,
      };

      // Only add status fields if status was changed
      if (is_active !== undefined) updateData.is_active = is_active;
      if (is_blocked !== undefined) updateData.is_blocked = is_blocked;

      // Remove undefined values
      Object.keys(updateData).forEach(
        (key) => updateData[key] === undefined && delete updateData[key]
      );

      const res = await apiService.put(`/admin/users/${user.id}`, updateData);

      if (res?.success) {
        // Refresh user data and call callback
        if (onUserUpdated) {
          onUserUpdated();
        }
        onClose();
      } else {
        setError(res?.error || "Failed to update user");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Backdrop onClick={onClose} />
      <ModalContainer>
        {/* Header */}
        <div className="z-10 flex items-center justify-between bg-white">
          <div>
            <h2 className="text-page-title font-rubik md:text-page-title-d text-esdarkGray font-semibold">
              User information
            </h2>
            <p className="text-page-subtitle font-normal mt-4 text-esDarkBlue opacity-50">
              Please edit user information and permissions below.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="h-9 w-9 rounded-[8px] hover:bg-[#F4F6F8] grid place-items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="#1E1E1E"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="py-8 text-center">
            <div className="text-sm text-gray-500">Loading user data...</div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="py-8">
            <div className="bg-red-50 border border-red-200 rounded-[8px] p-4">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          </div>
        )}

        {/* Content - only show if not loading and no error */}
        {!loading && !error && userData && (
          <>
            {/* Block/Unblock button */}
            {userData && (
              <div className="mt-8">
                {userData.is_blocked && (
                  <button className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-[8px] bg-[#B9FFCC] text-[#16653F] border border-[#A0E8B8]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M20 6 9 17l-5-5"
                        stroke="#16653F"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    User is blocked
                  </button>
                )}
              </div>
            )}

            {/* Basic information */}
            <div className="space-y-8 py-8">
              <section className="space-y-4">
                <SectionTitle>Basic information</SectionTitle>
                <div className="grid grid-cols-1 gap-4">
                  <Field label="Name">
                    <input
                      type="text"
                      value={form.firstName || "N/A"}
                      onChange={(e) =>
                        setForm({ ...form, firstName: e.target.value })
                      }
                      className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                      placeholder="Name"
                    />
                  </Field>
                  <Field label="Last name">
                    <input
                      type="text"
                      value={form.lastName || "N/A"}
                      onChange={(e) =>
                        setForm({ ...form, lastName: e.target.value })
                      }
                      className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                      placeholder="Last name"
                    />
                  </Field>
                  <Field label="Role">
                    <select
                      value={form.role}
                      onChange={(e) =>
                        setForm({ ...form, role: e.target.value })
                      }
                      className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                    >
                      <option value="">Select role</option>
                      <option value="buyer">Buyer</option>
                      <option value="seller">Seller</option>
                      <option value="admin">Admin</option>
                    </select>
                  </Field>
                  <Field label="Status">
                    <select
                      value={form.status}
                      onChange={(e) =>
                        setForm({ ...form, status: e.target.value })
                      }
                      className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                    >
                      <option value="">Select status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </Field>
                </div>
              </section>
            </div>

            {/* Log history */}
            <div className="mt-0">
              <SectionTitle>Log history</SectionTitle>
              <div className="space-y-4">
                {logsLoading ? (
                  <div className="py-4 text-center text-gray-500 text-sm">
                    Loading log history...
                  </div>
                ) : userLogs.length === 0 ? (
                  <div className="py-4 text-center text-gray-500 text-sm">
                    {userData?.role === "admin" ||
                    userData?.role === "super_admin"
                      ? "No admin actions found for this user"
                      : "This user has no admin action logs"}
                  </div>
                ) : (
                  userLogs.map((notification) => (
                    <div
                      key={notification.id}
                      className="flex items-center justify-between gap-4 flex-wrap"
                    >
                      <div className="flex flex-col gap-2">
                        <div className="flex items-start gap-2">
                          <span className="text-esblue mt-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                cx="11"
                                cy="11"
                                r="8"
                                stroke="#2A8BF7"
                                strokeWidth="2"
                              />
                              <path
                                d="m21 21-4.3-4.3"
                                stroke="#2A8BF7"
                                strokeWidth="2"
                              />
                            </svg>
                          </span>
                          <div>
                            <div className="text-[14px] font-[600] opacity-75 text-[#1E1E1E]">
                              {notification.title}
                            </div>
                          </div>
                        </div>
                        <div className="text-[16px] font-[400] opacity-50 text-[#000025]">
                          by {getAdminDisplayName(notification.user)}
                        </div>
                      </div>
                      <div className="text-[12px] font-[500] opacity-50 text-[#000025] whitespace-nowrap">
                        {formatRelativeTime(notification.created_at)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-[#F0F6FF] rounded-bl-[16px] bg-white mt-8">
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={onClose}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-[8px] text-[#FF6E6E] text-[14px] font-[600] bg-[#FFD9D9]"
                >
                  Discard changes
                </button>
                <button
                  onClick={handleSaveChanges}
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-[8px] text-white text-[14px] font-[600] bg-blue-gradient disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Saving..." : "Save changes"}
                </button>
              </div>
            </div>
          </>
        )}
      </ModalContainer>
    </>
  );
};

export default UserInfoModal;
